import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ModifierModal from "@/components/ModifierModal";
import { useCart } from "@/context/CartContext";
import { useContent } from "@/context/ContentContext";
import { MENU } from "@/constants/testIds";
import { toast } from "sonner";
import { Search } from "lucide-react";

export default function OrderOnline() {
    const c = useContent();
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [active, setActive] = useState("");
    const [search, setSearch] = useState("");
    const [selectedItem, setSelectedItem] = useState(null);
    const { addLine } = useCart();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        client.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
        client.get("/menu").then((r) => setItems(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const cat = searchParams.get("category");
        if (cat) setActive(cat);
    }, [searchParams]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        let list = items;
        if (q) list = list.filter((i) => i.name.toLowerCase().includes(q) || (i.description || "").toLowerCase().includes(q));
        return list;
    }, [items, search]);

    const grouped = useMemo(() => {
        const map = {};
        for (const it of filtered) {
            if (!map[it.category]) map[it.category] = [];
            map[it.category].push(it);
        }
        return map;
    }, [filtered]);

    const scrollTo = (slug) => {
        setActive(slug);
        const el = document.getElementById(`cat-${slug}`);
        if (el) {
            const y = el.getBoundingClientRect().top + window.scrollY - 96;
            window.scrollTo({ top: y, behavior: "smooth" });
        }
    };

    const handleSelect = (item) => {
        if (!item.modifiers || item.modifiers.length === 0) {
            addLine({
                item_id: item.id,
                name: item.name,
                price: Number(item.price),
                quantity: 1,
                modifiers: [],
                line_total: Number(item.price),
            });
            toast.success(`${item.name} added`);
            return;
        }
        setSelectedItem(item);
    };

    const handleAdd = (line) => {
        addLine(line);
        toast.success("Added to cart");
    };

    return (
        <div className="container-wide py-14">
            <div className="mb-8">
                <p className="font-script text-brand-gold text-3xl">{c.menu_page?.script || "The Menu"}</p>
                <h1 className="font-display text-4xl sm:text-5xl mt-1 leading-tight">{c.menu_page?.title || "Menu"}</h1>
                <p className="mt-3 text-brand-mute max-w-xl">
                    {c.menu_page?.subtitle || "Ready-to-eat & retail. Pickup at 33 Heronmere Road, Reuven — usually ready in ~30 minutes."}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
                {/* Sticky side-nav */}
                <aside className="hidden lg:block">
                    <div className="sticky top-24">
                        <div className="relative mb-4">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mute" />
                            <input
                                data-testid={MENU.searchInput}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search menu…"
                                className="w-full bg-brand-charcoal border border-brand-line text-sm text-brand-cream pl-9 pr-3 py-2 rounded-sm focus:outline-none focus:border-brand-olive"
                            />
                        </div>
                        <nav className="flex flex-col gap-1 border-l border-brand-line">
                            {categories.map((c) => (
                                <button
                                    key={c.slug}
                                    data-testid={`${MENU.categoryPrefix}${c.slug}`}
                                    onClick={() => scrollTo(c.slug)}
                                    className={`text-left text-sm px-4 py-2 border-l-2 -ml-[1px] transition-colors ${
                                        active === c.slug
                                            ? "border-brand-gold text-brand-gold"
                                            : "border-transparent text-brand-cream/70 hover:text-brand-cream"
                                    }`}
                                >
                                    {c.name}
                                </button>
                            ))}
                        </nav>
                    </div>
                </aside>

                {/* Mobile category chips */}
                <div className="lg:hidden -mx-6 px-6 sticky top-16 z-30 bg-brand-ink/90 backdrop-blur border-b border-brand-line py-3 overflow-x-auto">
                    <div className="flex gap-2 w-max">
                        {categories.map((c) => (
                            <button
                                key={c.slug}
                                data-testid={`${MENU.categoryPrefix}${c.slug}-m`}
                                onClick={() => scrollTo(c.slug)}
                                className={`whitespace-nowrap text-xs px-3 py-1.5 border rounded-sm transition-colors ${
                                    active === c.slug
                                        ? "border-brand-gold text-brand-gold"
                                        : "border-brand-line text-brand-cream/80"
                                }`}
                            >
                                {c.name}
                            </button>
                        ))}
                    </div>
                </div>

                <main className="space-y-14">
                    <div className="lg:hidden relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mute" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search menu…"
                            className="w-full bg-brand-charcoal border border-brand-line text-sm text-brand-cream pl-9 pr-3 py-2 rounded-sm focus:outline-none focus:border-brand-olive"
                        />
                    </div>
                    {categories.map((c) => {
                        const list = grouped[c.slug];
                        if (!list || list.length === 0) return null;
                        return (
                            <section key={c.slug} id={`cat-${c.slug}`} className="scroll-mt-28">
                                <div className="flex items-baseline justify-between mb-6">
                                    <div>
                                        <p className="font-script text-brand-gold text-4xl sm:text-5xl leading-none">
                                            {c.name}
                                        </p>
                                        {c.description && (
                                            <p className="text-brand-mute text-sm mt-1">{c.description}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                                    {list.map((item) => (
                                        <ProductCard key={item.id} item={item} onSelect={handleSelect} />
                                    ))}
                                </div>
                            </section>
                        );
                    })}
                </main>
            </div>

            <ModifierModal
                open={!!selectedItem}
                onOpenChange={(o) => !o && setSelectedItem(null)}
                item={selectedItem}
                onAdd={handleAdd}
            />
        </div>
    );
}
